import { useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Monitor, ScanLine, TrendingUp, Search, CheckCircle, ClipboardList, ArrowRight, Pencil, Trash2, ChevronRight, Eye, EyeOff, Play, MoreVertical, Users, GraduationCap, Square, AlertTriangle, Calendar, Lock, Unlock, Printer, RotateCcw } from 'lucide-react';
import { useClasseContext } from '../context/ClasseContext';
import { useUser } from '../context/UserContext';
import { isInPeriode } from '../utils/periode';
import useConfirm from '../hooks/ui/useConfirm';
import { useEval } from '../hooks/fetch_api_hook/useEval';
import { useSessionsQCM } from '../hooks/fetch_api_hook/useSessionsQCM';
import { useEvalNat } from '../hooks/fetch_api_hook/useEvalNat';
import { useCartesAruco } from '../hooks/fetch_api_hook/useCartesAruco';
import { useAssignationsCarte } from '../hooks/fetch_api_hook/useAssignationsCarte';
import { usePostApi } from '../hooks/post_api_hook/usePostApi';
import useDeleteApi from '../hooks/delete_api_hook/useDeleteApi';
import { useDeleteEval } from '../hooks/delete_api_hook/useDeleteEval';
import { useDeleteEvalNat } from '../hooks/delete_api_hook/useDeleteEvalNat';
import useUpdateGroupe from '../hooks/update_api_hook/useUpdateGroupe';
import axios from '../interceptors/axios';
import { url } from '../utils/confserveur';
import { buildEvaluationRows, sessionPhase, bilanPhase } from '../utils/evaluationRows';
import { toPayloadsUnifies } from '../utils/evalNatAdapter';
import { formatLettresDate } from '../utils/ClasseEvaluations';
import Loading from '../components/general/Loading';
import EvalDetail from '../components/VueEvaluation/EvalDetail';
import EditEval from '../components/FormEvaluation/EditEval';
import LaunchModal from '../components/VueQuestion/LaunchModal';
import SessionPreviewModal from '../components/VueQuestion/SessionPreviewModal';
import EditSessionModal from '../components/VueQuestion/EditSessionModal';
import ModalEquipesQCM from '../components/VueQuestion/ModalEquipesQCM';
import ModalCompetencesQCM from '../components/VueQuestion/ModalCompetencesQCM';

/**
 * Liste Évaluations unifiée — Phases 3.1 (lecture) + 3.2 (actions).
 *
 * Agrège les 3 sources via les hooks existants (zéro endpoint nouveau) :
 *   - évals classiques  → useEval by-etablissement (règle d'Evaluations.jsx :
 *     créateur + classe/groupe) ;
 *   - sessions QCM      → useSessionsQCM (ArUco / en ligne, statut natif) ;
 *   - bilans EvalNat    → useEvalNat (nationales importées EXCLUES — elles
 *     vivent dans Pilotage → Résultats nationaux).
 * La normalisation vit dans utils/evaluationRows.js (adaptateur par source).
 *
 * Actions par ligne (3.2 + consolidation des historiques) — la page remplace
 * l'ancienne liste (evals/:id redirige ici), l'historique d'EvalBilan ET
 * l'historique sessions de QCMDashboard (poste de pilotage unique) :
 *   - classique : clic → saisie (EvalDetail monté ici, piloté par ?eval= —
 *     URL partageable, bouton retour navigateur OK), Modifier (EditEval),
 *     Supprimer (hors devoirs communs, comme l'ancienne page) ;
 *   - session   : clic → résultats (ou aperçu questions si en_attente),
 *     le glyphe en bout de ligne (œil = fenêtre, chevron = page) fait pareil ;
 *     Projeter (bouton principal, cartes prêtes/en cours) + menu ⋯
 *     (Modifier : titre et date en attente, titre seul ensuite ; Équipes,
 *     correction, Noter ou Changer les compétences, Terminer, Supprimer —
 *     conditions de l'ex-SessionCard) ;
 *   - bilan     : clic → détail (eval-bilan/:id?bilan=<id>) ; Modifier (non
 *     commencé) → LaunchModal monté ICI (les questions complètes arrivent déjà
 *     dans le fetch, même serializer qu'EvalBilan — zéro requête en plus) ;
 *     Supprimer ; correction visible/masquée.
 */

// Hoistés : identités stables entre renders (props du LaunchModal)
const FORMATS_EVALNAT = ['en_ligne', 'papier'];
const LISTE_VIDE = [];

// Extraction de message d'erreur backend (les vues sessions renvoient
// {error: '...'} ; le wizard {detail}/{titre}/{non_field_errors}).
const extractErrorMsg = (err) => {
    const data = err?.response?.data;
    if (typeof data === 'string') return data;
    if (Array.isArray(data)) return data.join(' ');
    return data?.error || data?.non_field_errors?.join(' ') || data?.titre?.join(' ') || data?.detail || 'Erreur lors de la sauvegarde.';
};

/**
 * Menu ⋯ générique d'une ligne — toutes les sources (suivi de notes, session,
 * bilan) partagent ce burger pour aligner la colonne d'actions. Portal pour
 * échapper à l'overflow de la ligne.
 * @param {Array} items - [{ key, label, icon, onClick, classes?, disabled? }] (les falsy sont ignorés)
 */
const RowKebab = ({ items }) => {
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const visibles = (items || []).filter(Boolean);
    if (visibles.length === 0) return null;
    return (
        <span className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
                ref={btnRef}
                onClick={(e) => { e.stopPropagation(); setOpen((p) => !p); }}
                title="Actions"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            >
                <MoreVertical size={15} />
            </button>
            {open ? createPortal(
                <>
                    <div className="fixed inset-0 z-[9998]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
                    <div
                        className="fixed z-[9999] bg-white rounded-xl shadow-xl border border-gray-200 py-1 min-w-[175px]"
                        style={(() => {
                            const rect = btnRef.current?.getBoundingClientRect();
                            if (!rect) return {};
                            // Clamp bas de viewport : si le menu n'a pas la
                            // place sous le bouton, il s'ancre au-dessus.
                            const hauteurMenu = visibles.length * 34 + 10;
                            const top = rect.bottom + 4 + hauteurMenu > window.innerHeight
                                ? Math.max(8, rect.top - 4 - hauteurMenu)
                                : rect.bottom + 4;
                            return { top, right: window.innerWidth - rect.right };
                        })()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {visibles.map((it) => (
                            <button
                                key={it.key}
                                disabled={it.disabled}
                                onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick(); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 transition disabled:opacity-50 ${it.classes || 'text-gray-700'}`}
                            >
                                {it.icon}{it.label}
                            </button>
                        ))}
                    </div>
                </>,
                document.body
            ) : null}
        </span>
    );
};

/** Items du burger pour une session — conditions reprises de l'ex-SessionCard. */
const itemsSession = (session, h) => {
    const { statut, mode, mode_passation } = session;
    const isEnLigne = mode_passation === 'en_ligne';
    // Impression des cartes : déplacée dans l'en-tête de la page (geste PAR
    // CLASSE, décision prof 2026-09-01) — plus d'entrée par séance.
    // Noter exige une session TERMINÉE (contrat backend) — pour TOUS les modes.
    // L'ex-exception « en ligne + en_cours » menait à un 400 (le backend
    // refuse de noter une session non close).
    const peutNoter = statut === 'terminee';
    // « Rejouer la correction » (D11) : toute séance cartes close, quel que
    // soit son réglage de correction (chantier jeton, 2026-09-04) : le serveur
    // accepte `fin → revue` dans les deux modes, et revoir les questions après
    // coup vaut autant pour une séance corrigée au fil de l'eau. Seule
    // condition : l'étape vaut `fin`. Une séance close AVANT la migration 0067
    // a gardé `etape='quiz'` : l'endpoint lui répondrait 400, et D19 dit que
    // son écran ne change pas.
    const peutRejouerCorrection = !isEnLigne
        && statut === 'terminee'
        && session.etape === 'fin';
    // Plus d'« Aperçu questions » ici (2026-09-05) : l'œil en bout de ligne
    // l'ouvre sur une séance en attente, et la page résultats montre chaque
    // énoncé sur une séance close. Une destination, une commande.
    return [
        // « Modifier » dans tous les états (demande du prof, 2026-10-04) : une
        // séance partie ne change plus que de titre (EditSessionModal), le
        // serveur garde ses questions, sa classe et sa date.
        { key: 'edit', icon: <Pencil size={13} />, label: 'Modifier', onClick: () => h.onEdit(session) },
        peutRejouerCorrection && {
            key: 'rejouer-correction',
            icon: <RotateCcw size={13} />,
            label: 'Rejouer la correction',
            classes: 'text-blue-600',
            onClick: () => h.onRejouerCorrection(session),
        },
        !isEnLigne && mode === 'equipe' && statut === 'en_attente' && { key: 'equipes', icon: <Users size={13} />, label: 'Équipes', onClick: () => h.onEquipes(session) },
        isEnLigne && {
            key: 'correction',
            icon: session.correction_visible ? <EyeOff size={13} /> : <Eye size={13} />,
            label: session.correction_visible ? 'Masquer correction' : 'Afficher correction',
            onClick: () => h.onToggleCorrection(session),
        },
        peutNoter && !session.evaluation && { key: 'noter', icon: <GraduationCap size={13} />, label: 'Noter', classes: 'text-indigo-600', onClick: () => h.onNoter(session) },
        // Séance notée : une carte mal levée se corrige dans la page résultats,
        // onglet « Suivi par compétence » (même grille de saisie que les autres
        // évaluations). Pas d'entrée ici : ce serait un second chemin vers le
        // même écran (2026-09-05). Le seul autre but du prof est de refaire la
        // note avec d'autres compétences : un geste, dénoter puis noter
        // enchaînés derrière la modale. « Dénoter » seul (un état « close sans
        // note » que personne ne veut) a disparu ; sortir une séance du suivi,
        // c'est la supprimer, l'évaluation part avec elle côté serveur.
        peutNoter && session.evaluation && { key: 'changer-competences', icon: <GraduationCap size={13} />, label: 'Changer les compétences', classes: 'text-indigo-600', onClick: () => h.onChangerCompetences(session) },
        // Clôturer une session en cours (cartes OU en ligne) → la rend notable.
        // Le backend `end` accepte les deux modes.
        statut === 'en_cours' && {
            key: 'terminer',
            icon: <Square size={13} />,
            label: isEnLigne ? 'Clôturer' : 'Terminer',
            classes: 'text-orange-600',
            onClick: () => h.onTerminate(session),
        },
        { key: 'delete', icon: <Trash2 size={13} />, label: 'Supprimer', classes: 'text-red-600', onClick: () => h.onDelete(session) },
    ];
};

// Badge de type — reprend les conventions posées en Phase 2 (icônes de la
// barre sticky, violet EvalNat du LaunchModal).
const BADGE_CONFIG = {
    saisie: { label: 'Suivi de notes', Icone: FileText, classes: 'bg-blue-50 text-blue-700 border-blue-200' },
    qr_code: { label: 'Cartes en classe', Icone: ScanLine, classes: 'bg-amber-50 text-amber-700 border-amber-200' },
    en_ligne: { label: 'En ligne', Icone: Monitor, classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    bilan: { label: 'Bilan EvalNat', Icone: TrendingUp, classes: 'bg-violet-50 text-violet-700 border-violet-200' },
};

// Statut des évals classiques (suivi de notes) : il ne s'agit pas d'une
// passation mais d'un état de SAISIE. « en_attente » = le prof n'a pas
// verrouillé → il peut encore saisir ; « terminee » = verrouillé.
const CLASSIQUE_STATUT_CONFIG = {
    en_attente: { label: 'Saisie ouverte', classes: 'bg-amber-100 text-amber-700', Icone: Unlock },
    terminee: { label: 'Verrouillée', classes: 'bg-emerald-100 text-emerald-700', Icone: Lock },
};

// Phases du cycle de vie d'une PASSATION (sessions QCM ET bilans EvalNat —
// un bilan est une passation en ligne, même langage). Chaque phase a son
// badge distinct pour que l'état soit lisible d'un coup d'œil. « Programmée »
// (violet) est l'apport clé : elle n'est PAS confondue avec « À lancer ».
const PHASE_CONFIG = {
    programmee: { label: 'Programmée', classes: 'bg-violet-100 text-violet-700', Icone: Calendar },
    prete: { label: 'À lancer', classes: 'bg-amber-100 text-amber-700', Icone: Play },
    ouverte: { label: 'Ouverte aux élèves', classes: 'bg-teal-100 text-teal-700', Icone: Monitor },
    en_cours: { label: 'En cours', classes: 'bg-blue-100 text-blue-700', pulse: true },
    terminee: { label: 'Terminée', classes: 'bg-emerald-100 text-emerald-700' },
};

// Barre de répartition des savoir-faire (évals classiques) — mêmes 7 segments
// et mêmes couleurs d'acquisition que la page Evaluations historique.
const SEGMENTS_REPARTITION = [
    ['nb_vert_plus', 'bg-green-800'],
    ['nb_vert', 'bg-green-500'],
    ['nb_vert_pm', 'bg-teal-200'],
    ['nb_yellow', 'bg-yellow-500'],
    ['nb_orange', 'bg-orange-600'],
    ['nb_rouge', 'bg-red-600'],
    ['nb_no_scores', 'bg-zinc-100'],
];

const RepartitionSavoirFaires = ({ sf }) => {
    if (!sf) return null;
    const total = SEGMENTS_REPARTITION.reduce((s, [k]) => s + (sf[k] || 0), 0) || 1;
    return (
        <div
            className="hidden md:flex items-center h-4 w-40 bg-white rounded border border-gray-200 overflow-hidden flex-shrink-0"
            title="Répartition des acquisitions (savoir-faire)"
        >
            {SEGMENTS_REPARTITION.map(([k, couleur]) => (
                <div key={k} className={`${couleur} h-full`} style={{ width: `${((sf[k] || 0) / total) * 100}%` }} />
            ))}
        </div>
    );
};

const FILTRES = [
    { id: 'tous', label: 'Toutes' },
    { id: 'saisie', label: 'Suivi de notes' },
    { id: 'qr_code', label: 'Cartes' },
    { id: 'en_ligne', label: 'En ligne' },
    { id: 'bilan', label: 'Bilans EvalNat' },
];

/** Ligne de détails secondaires (questions, élèves, moyenne, durée, réponses). */
// Dernier élément de la ligne avant le ⋯ : il dit où mène la ligne (règle
// « la ligne montre, le bouton fait », 2026-09-05). Un œil = une fenêtre, on
// reste ; un chevron = une page, on part. Même destination que le clic de
// ligne. Le doublon à éviter, c'est deux commandes pour UNE destination :
// sur une séance en cartes en cours, le clic de ligne et le bouton
// « Projeter » mènent tous deux à la projection, donc pas de glyphe. Sur une
// séance prête, l'œil (les questions) et « Projeter » (la projection) sont
// deux destinations : les deux restent.
const glypheDeLigne = (row, phase) => {
    if (row.source === 'classique') return { Icone: ChevronRight, titre: 'Ouvrir la saisie' };
    if (row.source === 'bilan') return { Icone: ChevronRight, titre: 'Ouvrir le bilan' };
    const enLigne = row.original.mode_passation === 'en_ligne';
    if (phase === 'terminee') return { Icone: ChevronRight, titre: 'Voir les résultats' };
    if (phase === 'en_cours') return enLigne ? { Icone: ChevronRight, titre: 'Voir les résultats en direct' } : null;
    return { Icone: Eye, titre: 'Voir les questions' };
};

const metaDeLigne = (row) => {
    const parts = [];
    const m = row.meta || {};
    if (m.nbQuestions != null) parts.push(`${m.nbQuestions} question${m.nbQuestions > 1 ? 's' : ''}`);
    if (row.source === 'classique') {
        if (m.nbEleves != null) parts.push(`${m.nbEleves} élève${m.nbEleves > 1 ? 's' : ''}`);
        parts.push(m.moyenne != null ? `moyenne ${parseFloat(m.moyenne.toFixed(2))}` : 'pas encore de note');
    }
    if (m.duree) parts.push(`${m.duree} min`);
    // Répondants : bilans (toujours) + sessions EN LIGNE (eleves_reponses =
    // toute la classe). En ArUco, les « réponses » = scans → non pertinent ici.
    const enLigne = row.source === 'session' && row.original?.mode_passation === 'en_ligne';
    if ((row.source === 'bilan' || enLigne) && m.nbEleves > 0) {
        parts.push(`${m.nbReponses}/${m.nbEleves} répondants`);
    }
    if (m.equipe) parts.push('par équipes');
    return parts.join(' · ');
};

const EvaluationsUnifiees = () => {
    const { idclasse } = useParams();
    const navigate = useNavigate();
    const { classeNom, groupNom, ActivePeriode, classeDetail } = useClasseContext();
    const { etablissementData, user, selectedEtablissementId } = useUser();

    const estClasse = classeNom !== '';
    const nomRef = classeNom || groupNom || '';

    const [recherche, setRecherche] = useState('');
    const [filtre, setFiltre] = useState('tous');

    // 3.2 — flux classique porté par la page : saisie pilotée par ?eval=,
    // édition en modal, suppression avec refetch.
    const [searchParams, setSearchParams] = useSearchParams();
    const evalSelectionneeId = parseInt(searchParams.get('eval'), 10) || null;
    const [editEvalId, setEditEvalId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const { confirm, ConfirmDialog } = useConfirm();
    const { deleteEvaluation, isLoading: suppressionEnCours } = useDeleteEval();

    // Actions bilans (consolidation historiques) : suppression, correction
    // visible/masquée, édition d'un bilan non commencé (LaunchModal in-page).
    const { deleteEvaluationNat } = useDeleteEvalNat();
    const { updateGroupe } = useUpdateGroupe();
    const [togglingId, setTogglingId] = useState(null);
    const [editingBilan, setEditingBilan] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState(null);

    // Actions sessions (consolidation acte 2 — ex-historique QCMDashboard)
    const [, doPost] = usePostApi(null, null);
    const { deleteData } = useDeleteApi(null);
    const [editingSession, setEditingSession] = useState(null);
    const [editSessionLoading, setEditSessionLoading] = useState(false);
    const [editSessionError, setEditSessionError] = useState(null);
    const [sessionEquipes, setSessionEquipes] = useState(null);
    const [sessionToNote, setSessionToNote] = useState(null);
    const [previewSession, setPreviewSession] = useState(null);
    // Erreur d'une action de ligne (notation, reprise de correction) : une
    // seule bannière, le geste suivant remplace le message du précédent.
    const [actionError, setActionError] = useState(null);

    // Cartes ArUco + assignations : gating du bouton « Lancer » des sessions
    // ArUco en attente (mêmes hooks/calculs que QCMDashboard).
    const { data: rawCartes } = useCartesAruco(refreshKey);
    const { data: rawAssignations } = useAssignationsCarte(idclasse, refreshKey);
    const cartes = useMemo(() => (Array.isArray(rawCartes) ? rawCartes : (rawCartes?.results || [])), [rawCartes]);
    const assignations = useMemo(() => (Array.isArray(rawAssignations) ? rawAssignations : (rawAssignations?.results || [])), [rawAssignations]);
    const eleves = useMemo(() => (Array.isArray(classeDetail?.eleves) ? classeDetail.eleves : []), [classeDetail]);
    const hasEnoughCartes = cartes.length >= eleves.length;
    // En mode groupe, les assignations sont portées par les classes d'origine
    // → on ne bloque pas (même règle que QCMDashboard).
    const allElevesAssigned = useMemo(() => {
        if (groupNom || eleves.length === 0) return true;
        const assignes = new Set(assignations.map((a) => a.eleve));
        return eleves.every((e) => assignes.has(e.id));
    }, [groupNom, eleves, assignations]);

    // Date du jour (YYYY-MM-DD) — base de la phase « programmée » des sessions
    const today = new Date().toISOString().split('T')[0];

    // ── Les 3 fetchs partent en parallèle au montage (pas de waterfall) ──
    // refresh : à l'ouverture/fermeture de la saisie (stats fraîches après
    // saisie des notes) et après édition/suppression.
    const { data: evals, isLoading: loadingEvals } = useEval(
        `by-etablissement/?etablissement_id=${etablissementData?.id}`,
        `${evalSelectionneeId || ''}-${refreshKey}`
    );
    // refreshKey (pas null) : sinon les actions session (modifier/supprimer/
    // noter/correction) ne rafraîchissent pas la liste — useApi ne refetch que
    // si [url, refresh, enabled] change, et l'url est stable (idclasse constant).
    const { data: sessions, isLoading: loadingSessions } = useSessionsQCM(
        estClasse ? { classe: idclasse } : { groupe: idclasse },
        refreshKey
    );
    // Bilans : endpoint différent selon classe (classes_pk) ou groupe
    // (etablissement_id + groupe_id) — même mécanique qu'EvalNatLoader.
    const { data: evalsNatClasse, isLoading: loadingBilansClasse } = useEvalNat(
        null, parseInt(idclasse, 10), refreshKey, !estClasse && 'not-enabled'
    );
    const { data: evalsNatGroupe, isLoading: loadingBilansGroupe } = useEvalNat(
        etablissementData?.id ? `?etablissement_id=${etablissementData.id}&groupe_id=${idclasse}` : null,
        null, refreshKey, (estClasse || !etablissementData?.id) && 'not-enabled'
    );

    // ── Filtrage par source (règles des pages d'origine) ──
    const evalsClassiques = useMemo(() => {
        if (!Array.isArray(evals)) return [];
        return evals.filter((ev) =>
            ev.createur === user?.id &&
            (estClasse ? ev.classe_nom === classeNom : ev.groupe_nom === groupNom)
        );
    }, [evals, user, estClasse, classeNom, groupNom]);

    const bilans = useMemo(() => {
        const src = estClasse ? evalsNatClasse : evalsNatGroupe;
        if (!Array.isArray(src)) return [];
        const idNum = parseInt(idclasse, 10);
        return src.filter((e) => (estClasse ? e.classe === idNum : e.groupe === idNum));
    }, [estClasse, evalsNatClasse, evalsNatGroupe, idclasse]);

    const sessionsList = useMemo(() => (Array.isArray(sessions) ? sessions : []), [sessions]);

    // ── Agrégation + tri (déduplication sessions notées incluse) ──
    const rows = useMemo(
        () => buildEvaluationRows({ evalsClassiques, sessions: sessionsList, bilans }),
        [evalsClassiques, sessionsList, bilans]
    );

    // ── Filtre période active (toutes sources) — mêmes bornes que la page
    // Evaluations historique. Les lignes SANS date (ex : session programmée
    // sans date de passation) restent visibles.
    const rowsPeriode = useMemo(
        () => rows.filter((r) => isInPeriode(r.dateISO, ActivePeriode)),
        [rows, ActivePeriode],
    );

    const compteurs = useMemo(() => {
        const c = { tous: rowsPeriode.length, saisie: 0, qr_code: 0, en_ligne: 0, bilan: 0 };
        rowsPeriode.forEach((r) => { c[r.badge] = (c[r.badge] || 0) + 1; });
        return c;
    }, [rowsPeriode]);

    const rowsFiltrees = useMemo(() => {
        let r = rowsPeriode;
        if (filtre !== 'tous') r = r.filter((x) => x.badge === filtre);
        const term = recherche.trim().toLowerCase();
        if (term) r = r.filter((x) => x.titre.toLowerCase().includes(term));
        return r;
    }, [rowsPeriode, filtre, recherche]);

    // ── Actions par ligne (3.2) ──
    const evalSelectionnee = useMemo(() => {
        if (!evalSelectionneeId) return null;
        return evalsClassiques.find((e) => e.id === evalSelectionneeId) || null;
    }, [evalSelectionneeId, evalsClassiques]);

    // Clic ligne — action principale selon la source et, pour les sessions, la phase
    const ouvrirLigne = (row) => {
        if (row.source === 'classique') {
            setSearchParams({ eval: String(row.original.id) });
            return;
        }
        if (row.source === 'bilan') {
            navigate(`/home/eval-bilan/${idclasse}?bilan=${row.original.id}`);
            return;
        }
        // Session : la phase décide
        const s = row.original;
        const phase = sessionPhase(s, today);
        if (phase === 'en_cours') {
            // ArUco en cours → la projection, seul écran du PC (le téléphone se
            // relie par le QR, chantier jeton 2026-09-04) ; en ligne en cours →
            // page résultats (gère le live côté en ligne).
            if (s.mode_passation === 'en_ligne') navigate(`/home/qcm/${idclasse}/session/${s.id}/resultats`);
            else navigate(`/home/qcm/${idclasse}/session/${s.id}/projection`);
        } else if (phase === 'terminee') {
            navigate(`/home/qcm/${idclasse}/session/${s.id}/resultats`);
        } else {
            // programmee / prete / ouverte : pas encore de résultats → aperçu seul
            setPreviewSession(s);
        }
    };

    // ── Actions sessions (reprises de l'historique QCMDashboard, supprimé) ──

    // « Lancer » depuis le PC n'est plus qu'une NAVIGATION : on ouvre l'écran
    // de projection et la session reste `en_attente`. `start` et la première
    // question partent du smartphone, seul poste de pilotage — le PC projette.
    const lancerSession = (session) => navigate(`/home/qcm/${idclasse}/session/${session.id}/projection`);

    // Le jeu de 30 cartes du professeur, sans nom, sans récapitulatif
    // (contrat back du 2026-09-21) : imprimé une fois, il sert à toutes les
    // classes et tous les groupes ; la correspondance carte → élève se lit sur
    // l'écran projeté au lancement de chaque séance. Le jeu est par
    // établissement (les numéros s'y répètent) : le paramètre n'est requis que
    // si le professeur en a plusieurs.
    const imprimerCartes = () => {
        const plusieursEtabs = (user?.etablissements?.length || 0) > 1 && selectedEtablissementId;
        window.open(`${url}cartes-aruco/pdf/${plusieursEtabs ? `?etablissement=${selectedEtablissementId}` : ''}`, '_blank');
    };

    // Rejouer la correction d'une séance close (D11). Le geste repose l'étape à
    // `revue` sur la première question, puis ouvre la projection — la séance
    // reste `terminee`, c'est l'étape qui fait foi (D3). Le professeur rouvre
    // `.../live` de son côté : la phase 4 lui donne déjà « précédent / suivant »
    // sur une séance close, et la phase 1 lui garde le flux ouvert.
    //
    // On ne navigue QUE si le serveur a accepté la transition : sinon la
    // projection s'ouvrirait sur un écran de fin, sans rien dire de l'échec.
    const rejouerCorrection = async (session) => {
        setActionError(null);
        const result = await doPost(
            { etape: 'revue', revue_ordre: 1 },
            `${url}sessions-qcm/${session.id}/etape/`,
        );
        if (!result?.success) {
            setActionError(`Correction impossible à rejouer : ${extractErrorMsg(result?.error)}`);
            return;
        }
        navigate(`/home/qcm/${idclasse}/session/${session.id}/projection`);
    };

    const terminerSession = async (session) => {
        if (!(await confirm({ title: 'Terminer cette session ?', message: 'Les élèves ne pourront plus répondre.', confirmLabel: 'Terminer', variant: 'warning' }))) return;
        await doPost({}, `${url}sessions-qcm/${session.id}/end/`);
        setRefreshKey((k) => k + 1);
    };

    const supprimerSession = async (session) => {
        // Le serveur efface l'évaluation issue de la notation avec la séance
        // (2026-09-05) : le prof doit le savoir avant de confirmer.
        const message = session.evaluation
            ? 'Ses notes seront supprimées avec elle. Cette action est irréversible.'
            : 'Cette action est irréversible.';
        if (!(await confirm({ title: 'Supprimer cette session ?', message, confirmLabel: 'Supprimer', variant: 'danger' }))) return;
        await deleteData(`${url}sessions-qcm/${session.id}/`);
        setRefreshKey((k) => k + 1);
    };

    // PATCH titre/date — payload fourni par EditSessionModal
    const modifierSession = async (payload) => {
        if (!editingSession) return;
        setEditSessionError(null);
        setEditSessionLoading(true);
        try {
            await axios.patch(`${url}sessions-qcm/${editingSession.id}/`, payload);
            setEditingSession(null);
            setRefreshKey((k) => k + 1);
        } catch (err) {
            setEditSessionError(extractErrorMsg(err));
        } finally {
            setEditSessionLoading(false);
        }
    };

    const basculerCorrectionSession = async (session) => {
        try {
            await updateGroupe(`${url}sessions-qcm/${session.id}/`, { correction_visible: !session.correction_visible });
            setRefreshKey((k) => k + 1);
        } catch (err) {
            window.alert(extractErrorMsg(err));
        }
    };

    // Notation : refs requises ; compétences manquantes → ModalCompetencesQCM
    const noterSession = (session) => {
        setActionError(null);
        const qs = session?.questions || [];
        const questionsAvecRef = qs.filter((q) => q.banque_question?.attendu || q.banque_question?.savoir_faire);
        if (questionsAvecRef.length === 0) {
            setActionError(`Impossible de noter « ${session.titre} » : aucune question n'est liée à un attendu ou savoir-faire (la note de suivi s'appuie dessus).`);
            return;
        }
        // Garde-fou : sans réponse enregistrée, le backend renvoie « Aucune
        // réponse exploitable ». On le dit clairement avant d'envoyer.
        const reps = Array.isArray(session.eleves_reponses) ? session.eleves_reponses : [];
        const nbRepondants = reps.filter((e) => e.reponses != null).length;
        if (nbRepondants === 0) {
            setActionError(`Impossible de noter « ${session.titre} » : aucun élève n'y a encore répondu.`);
            return;
        }
        const allHaveComp = questionsAvecRef.every((q) => q.banque_question?.competence);
        if (allHaveComp) {
            doPost({}, `${url}sessions-qcm/${session.id}/noter/`).then((result) => {
                if (result?.success) { setRefreshKey((k) => k + 1); return; }
                setActionError(`Notation impossible : ${extractErrorMsg(result?.error)}`);
            });
            return;
        }
        setSessionToNote(session);
    };

    // « Changer les compétences » : même modale que « Noter » (elle liste les
    // questions dont la compétence n'est pas fixée en banque, presque toutes ;
    // une question laissée vide garde le défaut de la matière, comme la note
    // automatique). La validation enchaîne dénoter puis noter.
    const changerCompetences = (session) => {
        setActionError(null);
        setSessionToNote(session);
    };

    const confirmerNoter = async (competencesMap) => {
        if (!sessionToNote) return;
        const base = `${url}sessions-qcm/${sessionToNote.id}/`;
        setSessionToNote(null);
        if (sessionToNote.evaluation) {
            const retrait = await doPost({}, `${base}denoter/`);
            if (!retrait?.success) {
                setActionError(`Compétences inchangées : ${extractErrorMsg(retrait?.error)}`);
                return;
            }
        }
        const result = await doPost({ competences: competencesMap }, `${base}noter/`);
        setRefreshKey((k) => k + 1);
        if (result?.success) return;
        // Après un dénoter réussi, la ligne repropose « Noter » : l'état est
        // visible et rattrapable.
        setActionError(`Notation impossible : ${extractErrorMsg(result?.error)}`);
    };

    const supprimerEval = async (ev) => {
        if (!(await confirm({ title: 'Supprimer cette évaluation ?', confirmLabel: 'Supprimer', variant: 'danger' }))) return;
        await deleteEvaluation(ev.id);
        setRefreshKey((k) => k + 1);
    };

    // ── Actions bilans (reprises de l'historique EvalBilan, supprimé) ──

    const supprimerBilan = async (b) => {
        if (!(await confirm({ title: 'Supprimer ce bilan ?', message: 'Cette action est irréversible.', confirmLabel: 'Supprimer', variant: 'danger' }))) return;
        await deleteEvaluationNat(b.id);
        setRefreshKey((k) => k + 1);
    };

    const basculerCorrection = async (b) => {
        setTogglingId(b.id);
        try {
            await updateGroupe(`${url}evaluations_nat/${b.id}/`, { correction_visible: !b.correction_visible });
            setRefreshKey((k) => k + 1);
        } catch (err) {
            window.alert(err?.response?.data?.error || err?.response?.data?.detail || 'Une erreur est survenue.');
        }
        setTogglingId(null);
    };

    // Questions du bilan en édition, au format unifié du LaunchModal — les
    // objets complets arrivent déjà dans le fetch (même serializer
    // qu'EvalBilan), aucune requête supplémentaire.
    const payloadsEditionBilan = useMemo(
        () => toPayloadsUnifies(editingBilan?.questions),
        [editingBilan]
    );

    // PATCH d'un bilan non commencé — contrat LaunchModal (2.4b) : ids
    // ré-extraits de _meta.evalNatId dans l'ordre choisi au modal.
    const handleModifierBilan = async (config) => {
        setEditError(null);
        setEditLoading(true);
        const questionIds = (config.questions || []).map((q) => q._meta?.evalNatId).filter(Boolean);
        const date = config.datePassation || new Date().toISOString().slice(0, 10);
        try {
            await axios.patch(`${url}evaluations_nat/${editingBilan.id}/`, {
                titre: config.titre,
                date,
                duree: config.dureeQuiz,
                questions: questionIds,
                correction_visible: config.correctionVisible,
            });
            setEditingBilan(null);
            setRefreshKey((k) => k + 1);
        } catch (err) {
            const data = err?.response?.data;
            if (err?.response?.status === 403) {
                setEditError(data?.detail || 'Des élèves ont déjà répondu, modification impossible.');
            } else {
                setEditError(typeof data === 'string' ? data : data?.detail || data?.titre?.join(' ') || 'Erreur lors de la modification');
            }
        }
        setEditLoading(false);
    };

    const chargement = loadingEvals || loadingSessions || loadingBilansClasse || loadingBilansGroupe;
    if (suppressionEnCours) return <Loading loadingText={'Suppression en cours...'} />;
    if (chargement && rows.length === 0) return <Loading />;
    // Deep link ?eval= pendant que la liste charge encore
    if (evalSelectionneeId && !evalSelectionnee && loadingEvals) return <Loading />;

    // ── Saisie d'une éval classique — EvalDetail monté tel quel ; sa
    // fermeture (setSelectedEvalId(null)) vide le paramètre → retour liste.
    if (evalSelectionnee) {
        return (
            // Fond dégradé bleu (charte maquette — même fond que le hub Créer)
            <div className="flex-1 px-4 sm:px-6 py-8 lg:px-10 text-black bg-gradient-to-br from-[#EAF4FB] via-[#DCEFF9] to-[#D2EBF8] min-h-full">
                <div className="w-full max-w-[1400px]">
                <EvalDetail
                    selectedEval={{
                        titre: evalSelectionnee.titre,
                        date: evalSelectionnee.date,
                        statut: evalSelectionnee.statut,
                        nombre_eleves: evalSelectionnee.nombre_eleves,
                        moyenne_scores: evalSelectionnee.moyenne_scores,
                        savoir_faires: evalSelectionnee.savoir_faires,
                        eleves: evalSelectionnee.eleves,
                    }}
                    selectedEvalId={evalSelectionnee.id}
                    setSelectedEvalId={(val) => setSearchParams(val ? { eval: String(val) } : {})}
                />
                </div>
            </div>
        );
    }

    return (
        // Fond dégradé bleu (charte maquette — même fond que le hub Créer)
        <div className="flex-1 px-4 sm:px-6 py-8 lg:px-10 text-black bg-gradient-to-br from-[#EAF4FB] via-[#DCEFF9] to-[#D2EBF8] min-h-full">
            <div className="w-full max-w-[1400px]">
            {ConfirmDialog}
            {/* En-tête */}
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight">Évaluations</h1>
                    <p className="text-sm font-bold text-slate-500 mt-1">
                        {estClasse ? `Classe ${nomRef}` : `Groupe ${nomRef}`} · {rowsPeriode.length} évaluation{rowsPeriode.length > 1 ? 's' : ''} sur la période active
                        {chargement ? ' · mise à jour…' : ''}
                    </p>
                </div>
                {/* Le jeu de 30 cartes, toujours imprimable : il ne dépend
                    d'aucune séance ni d'aucune classe (décision prof 2026-09-21,
                    un seul jeu par prof, sans nom, la correspondance se lit à
                    l'écran le jour J). Un seul emplacement, retiré du menu ⋯
                    des lignes (décision prof 2026-09-01). */}
                <button
                    onClick={imprimerCartes}
                    title="Le jeu de 30 cartes du professeur, sans nom, à imprimer une fois. La correspondance carte ↔ élève s'affiche à l'écran au lancement de chaque séance."
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-white border border-gray-200 text-slate-600 hover:border-blue-300 hover:text-blue-700 shadow-sm transition-colors"
                >
                    <Printer size={14} /> Imprimer les 30 cartes
                </button>
            </div>

            {/* Barre filtres + recherche */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="flex flex-wrap items-center gap-1.5 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
                    {FILTRES.map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setFiltre(f.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                                filtre === f.id
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {f.label} ({compteurs[f.id] || 0})
                        </button>
                    ))}
                </div>
                <div className="relative ml-auto w-full sm:w-auto">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={recherche}
                        onChange={(e) => setRecherche(e.target.value)}
                        placeholder="Rechercher un titre…"
                        className="pl-9 pr-3 py-2 w-full sm:w-64 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-300/50 focus:border-blue-300 focus:outline-none"
                    />
                </div>
            </div>

            {/* Erreur d'action de ligne (notation impossible, reprise refusée) */}
            {actionError ? (
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-semibold mb-3">
                    <AlertTriangle size={15} className="flex-shrink-0" />
                    <span className="flex-1">{actionError}</span>
                    <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600 font-bold">×</button>
                </div>
            ) : null}

            {/* Liste */}
            {rowsFiltrees.length > 0 ? (
                <div className="space-y-2">
                    {rowsFiltrees.map((row) => {
                        const badge = BADGE_CONFIG[row.badge] || BADGE_CONFIG.saisie;
                        // Sessions : badge de PHASE (programmée/à lancer/ouverte/
                        // en cours/terminée) ; classiques & bilans : statut générique.
                        const phase = row.source === 'session' ? sessionPhase(row.original, today)
                            : row.source === 'bilan' ? bilanPhase(row.original, today)
                            : null;
                        // session/bilan → badge de phase ; classique → état de saisie
                        const statut = phase
                            ? PHASE_CONFIG[phase]
                            : (CLASSIQUE_STATUT_CONFIG[row.statut] || CLASSIQUE_STATUT_CONFIG.en_attente);
                        const meta = metaDeLigne(row);
                        const glyphe = glypheDeLigne(row, phase);
                        // Modifier/Supprimer : classiques uniquement, hors
                        // devoirs communs (même règle que l'ancienne page).
                        const modifiable = row.source === 'classique' && !/devoir commun/i.test(row.titre);
                        return (
                            <div
                                key={row.cle}
                                onClick={() => ouvrirLigne(row)}
                                className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex flex-wrap lg:flex-nowrap items-center gap-x-4 gap-y-1.5 shadow-sm cursor-pointer hover:border-blue-300 hover:shadow-md transition-all"
                            >
                                {/* Type (largeur fixe ≥ lg → colonnes alignées ;
                                    auto en dessous, la ligne wrappe) */}
                                <span className={`inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-bold w-auto lg:w-44 flex-shrink-0 ${badge.classes}`}>
                                    <badge.Icone size={13} />
                                    {badge.label}
                                </span>

                                {/* Titre + détails — pleine largeur sous lg (1re ligne) */}
                                <div className="order-first w-full lg:order-none lg:w-auto lg:flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate">{row.titre}</p>
                                    <div className="flex items-center gap-2 mt-0.5 min-w-0 overflow-hidden">
                                        {meta ? <span className="text-xs text-gray-500">{meta}</span> : null}
                                        {row.notee ? (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                                                <CheckCircle size={10} /> Notée
                                            </span>
                                        ) : null}
                                        {row.source === 'classique' ? (
                                            <RepartitionSavoirFaires sf={row.original.savoir_faires} />
                                        ) : null}
                                    </div>
                                </div>

                                {/* Statut / phase — colonne alignée (masquée < md) */}
                                <div className="hidden md:flex w-auto lg:w-44 flex-shrink-0 justify-start">
                                    {statut ? (
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold ${statut.classes}`}>
                                            {statut.pulse ? <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" /> : null}
                                            {statut.Icone ? <statut.Icone size={11} /> : null}
                                            {statut.label}
                                        </span>
                                    ) : null}
                                </div>

                                {/* Date — colonne alignée (masquée < sm) */}
                                <span className="hidden sm:block text-xs font-semibold text-gray-500 w-auto lg:w-40 lg:text-right whitespace-nowrap flex-shrink-0">
                                    {row.dateISO ? formatLettresDate(row.dateISO) : '—'}
                                </span>

                                {/* Actions — bouton principal (sessions) + burger ⋯ unifié, alignés à droite */}
                                <div className="w-auto sm:w-52 flex items-center justify-end gap-1.5 flex-shrink-0">
                                    {row.source === 'session' ? (() => {
                                        const s = row.original;
                                        const hasEquipes = s.mode !== 'equipe' || s.nb_equipes == null || s.nb_equipes > 0;
                                        // `phase` déjà calculée plus haut (badge) — pas de recompute
                                        // Les manques ne VERROUILLENT plus le lancement : ils
                                        // s'affichent en avertissement. Les cas particuliers se
                                        // règlent en séance depuis l'écran de projection, et
                                        // l'ancien tooltip renvoyait vers un onglet supprimé.
                                        const blocages = [];
                                        if (!hasEnoughCartes) blocages.push('pas assez de cartes générées');
                                        if (!allElevesAssigned) blocages.push("des élèves n'ont pas de carte");
                                        if (!hasEquipes) blocages.push('aucune équipe constituée');
                                        const sansReserve = blocages.length === 0;
                                        // Un seul bouton (chantier jeton, 2026-09-04) : le PC
                                        // projette ; le téléphone se relie en scannant le QR
                                        // affiché par la projection, il n'a plus de bouton ici.
                                        // Rouvrir une projection en cours de séance (PC planté,
                                        // changement de salle) passe par le même bouton.
                                        // L'avertissement cartes reste dessus.
                                        if ((phase === 'prete' || phase === 'en_cours') && s.mode_passation !== 'en_ligne') {
                                            return (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); lancerSession(s); }}
                                                    title={sansReserve
                                                        ? 'Ouvrir la projection sur cet écran'
                                                        : `Ouvrir la projection, à vérifier en séance : ${blocages.join(' · ')}`}
                                                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap flex-shrink-0 transition-colors ${
                                                        sansReserve
                                                            ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                                            : 'bg-amber-500 text-white hover:bg-amber-600'
                                                    }`}
                                                >
                                                    <Monitor size={13} />
                                                    {/* Le mot dit ce qui sera projeté : la
                                                        correction quand la séance en est là. */}
                                                    {phase === 'en_cours' && (s.etape === 'podium' || s.etape === 'revue')
                                                        ? 'Projeter la correction'
                                                        : 'Projeter'}
                                                </button>
                                            );
                                        }
                                        return null;
                                    })() : null}

                                    {/* Burger ⋯ unifié — items selon la source */}
                                    <RowKebab items={
                                        row.source === 'session'
                                            ? itemsSession(row.original, {
                                                onEdit: (x) => { setEditSessionError(null); setEditingSession(x); },
                                                onEquipes: setSessionEquipes,
                                                onToggleCorrection: basculerCorrectionSession,
                                                onNoter: noterSession,
                                                onChangerCompetences: changerCompetences,
                                                onTerminate: terminerSession,
                                                onDelete: supprimerSession,
                                                onRejouerCorrection: rejouerCorrection,
                                            })
                                            : row.source === 'bilan'
                                                ? [
                                                    {
                                                        key: 'correction',
                                                        icon: row.original.correction_visible ? <Eye size={13} /> : <EyeOff size={13} />,
                                                        label: row.original.correction_visible ? 'Masquer correction' : 'Afficher correction',
                                                        disabled: togglingId === row.original.id,
                                                        onClick: () => basculerCorrection(row.original),
                                                    },
                                                    row.statut === 'non_commence' && {
                                                        key: 'edit',
                                                        icon: <Pencil size={13} />,
                                                        label: 'Modifier',
                                                        onClick: () => { setEditError(null); setEditingBilan(row.original); },
                                                    },
                                                    { key: 'delete', icon: <Trash2 size={13} />, label: 'Supprimer', classes: 'text-red-600', onClick: () => supprimerBilan(row.original) },
                                                ]
                                                : modifiable
                                                    ? [
                                                        { key: 'edit', icon: <Pencil size={13} />, label: 'Modifier', onClick: () => setEditEvalId(row.original.id) },
                                                        { key: 'delete', icon: <Trash2 size={13} />, label: 'Supprimer', classes: 'text-red-600', onClick: () => supprimerEval(row.original) },
                                                    ]
                                                    : []
                                    } />
                                </div>

                                {/* Glyphe de destination (ou réserve de largeur
                                    quand le bouton « Projeter » tient ce rôle). */}
                                {glyphe ? (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); ouvrirLigne(row); }}
                                        title={glyphe.titre}
                                        aria-label={glyphe.titre}
                                        className="text-gray-400 hover:text-blue-600 flex-shrink-0 transition-colors"
                                    >
                                        <glyphe.Icone size={16} />
                                    </button>
                                ) : (
                                    <span className="w-4 flex-shrink-0" />
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-2xl py-14 flex flex-col items-center gap-3 text-center">
                    <ClipboardList size={36} className="text-gray-300" />
                    <p className="text-sm font-semibold text-gray-600">
                        {rows.length === 0
                            ? 'Aucune évaluation pour le moment.'
                            : rowsPeriode.length === 0
                                ? 'Aucune évaluation sur la période active (changez de période dans la barre latérale).'
                                : 'Aucune évaluation ne correspond à ces filtres.'}
                    </p>
                    {rows.length === 0 ? (
                        <Link
                            to={`/home/creer/${idclasse}`}
                            className="inline-flex items-center gap-1.5 text-sm font-extrabold text-blue-600 hover:text-blue-700 hover:underline"
                        >
                            Créer une évaluation <ArrowRight size={15} />
                        </Link>
                    ) : null}
                </div>
            )}

            {/* Modal d'édition (titre/date/savoirs) — refetch à la fermeture */}
            {editEvalId ? (
                <EditEval
                    ideval={editEvalId}
                    onClose={() => {
                        setEditEvalId(null);
                        setRefreshKey((k) => k + 1);
                    }}
                />
            ) : null}

            {/* Modal d'édition d'un bilan non commencé — LaunchModal unifié
                (2.4b), pré-rempli avec les questions du bilan, PATCH au
                lancement (mêmes props que l'édition côté EvalBilan). */}
            {editingBilan ? (
                <LaunchModal
                    launchMode="en_ligne"
                    availableFormats={FORMATS_EVALNAT}
                    selectedQuestions={payloadsEditionBilan}
                    withTitre
                    initialConfig={{
                        titre: editingBilan.titre,
                        dureeQuiz: editingBilan.duree,
                        datePassation: editingBilan.date,
                        correctionVisible: editingBilan.correction_visible,
                    }}
                    printContext={{ nomClasse: nomRef }}
                    cartes={LISTE_VIDE}
                    eleves={LISTE_VIDE}
                    isLoading={editLoading}
                    error={editError}
                    onLaunch={handleModifierBilan}
                    onClose={() => { setEditingBilan(null); setEditError(null); }}
                />
            ) : null}

            {/* ── Modals sessions (ex-historique QCMDashboard) ── */}
            {previewSession ? (
                <SessionPreviewModal session={previewSession} onClose={() => setPreviewSession(null)} />
            ) : null}
            {editingSession ? (
                <EditSessionModal
                    session={editingSession}
                    isLoading={editSessionLoading}
                    error={editSessionError}
                    onSave={modifierSession}
                    onClose={() => { setEditingSession(null); setEditSessionError(null); }}
                />
            ) : null}
            {sessionEquipes ? (
                <ModalEquipesQCM
                    session={sessionEquipes}
                    cartes={cartes}
                    eleves={eleves}
                    onClose={() => { setSessionEquipes(null); setRefreshKey((k) => k + 1); }}
                />
            ) : null}
            {sessionToNote ? (
                <ModalCompetencesQCM
                    session={sessionToNote}
                    matiere={user?.matiere || ''}
                    onConfirm={confirmerNoter}
                    onClose={() => setSessionToNote(null)}
                />
            ) : null}
            </div>
        </div>
    );
};

export default EvaluationsUnifiees;
